import { useState, useEffect } from 'react';
import { useNotifications } from '@/contexts/NotificationContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { 
  Bell, Mail, Smartphone, Clock, Moon, 
  FileText, CreditCard, GraduationCap, Award, Settings,
  Save, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'applications', label: 'Applications', icon: FileText, description: 'Application submissions and status updates' },
  { id: 'payments', label: 'Payments', icon: CreditCard, description: 'Payment confirmations and reminders' },
  { id: 'exams', label: 'Exams', icon: GraduationCap, description: 'Exam schedules, results, and reminders' },
  { id: 'certificates', label: 'Certificates', icon: Award, description: 'Certificate issuance and updates' },
  { id: 'system', label: 'System', icon: Settings, description: 'System announcements and maintenance' },
];

export default function NotificationPreferences() {
  const { preferences, updatePreferences } = useNotifications();
  const [saving, setSaving] = useState(false);
  const [localPrefs, setLocalPrefs] = useState({
    email_enabled: true,
    push_enabled: true,
    frequency: 'instant',
    quiet_hours_start: '',
    quiet_hours_end: '',
    categories: {
      applications: true,
      payments: true,
      exams: true,
      certificates: true,
      system: true
    }
  });

  useEffect(() => {
    if (preferences) {
      setLocalPrefs({
        email_enabled: preferences.email_enabled ?? true,
        push_enabled: preferences.push_enabled ?? true,
        frequency: preferences.frequency || 'instant',
        quiet_hours_start: preferences.quiet_hours_start || '',
        quiet_hours_end: preferences.quiet_hours_end || '',
        categories: preferences.categories || {
          applications: true,
          payments: true,
          exams: true,
          certificates: true,
          system: true
        }
      });
    }
  }, [preferences]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePreferences(localPrefs);
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setLocalPrefs(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [categoryId]: !prev.categories[categoryId]
      }
    }));
  };

  return (
    <Card className="w-full" data-testid="notification-preferences">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Configure how and when you receive notifications
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Notification Channels */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700">Notification Channels</h3>
          
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-slate-500" />
              <div>
                <Label className="text-sm font-medium">Email Notifications</Label>
                <p className="text-xs text-slate-500">Receive notifications via email</p>
              </div>
            </div>
            <Switch
              checked={localPrefs.email_enabled}
              onCheckedChange={(checked) => setLocalPrefs(prev => ({ ...prev, email_enabled: checked }))}
              data-testid="email-toggle"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Smartphone className="h-5 w-5 text-slate-500" />
              <div>
                <Label className="text-sm font-medium">Push Notifications</Label>
                <p className="text-xs text-slate-500">Receive real-time push notifications</p>
              </div>
            </div>
            <Switch
              checked={localPrefs.push_enabled}
              onCheckedChange={(checked) => setLocalPrefs(prev => ({ ...prev, push_enabled: checked }))}
              data-testid="push-toggle"
            />
          </div>
        </div>

        <Separator />

        {/* Frequency Settings */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Delivery Frequency
          </h3>
          
          <Select
            value={localPrefs.frequency}
            onValueChange={(value) => setLocalPrefs(prev => ({ ...prev, frequency: value }))}
          >
            <SelectTrigger className="w-full" data-testid="frequency-select">
              <SelectValue placeholder="Select frequency" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="instant">Instant - Receive immediately</SelectItem>
              <SelectItem value="daily">Daily Digest - Once per day at 9 AM</SelectItem>
              <SelectItem value="weekly">Weekly Summary - Every Monday</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Separator />

        {/* Quiet Hours */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Moon className="h-4 w-4" />
            Quiet Hours
          </h3>
          <p className="text-xs text-slate-500">Silence notifications during these hours (push only)</p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Start Time</Label>
              <Input
                type="time"
                value={localPrefs.quiet_hours_start}
                onChange={(e) => setLocalPrefs(prev => ({ ...prev, quiet_hours_start: e.target.value }))}
                placeholder="22:00"
                data-testid="quiet-start"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">End Time</Label>
              <Input
                type="time"
                value={localPrefs.quiet_hours_end}
                onChange={(e) => setLocalPrefs(prev => ({ ...prev, quiet_hours_end: e.target.value }))}
                placeholder="08:00"
                data-testid="quiet-end"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Category Preferences */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700">Notification Categories</h3>
          <p className="text-xs text-slate-500">Choose which types of notifications you want to receive</p>
          
          <div className="space-y-2">
            {CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <div 
                  key={category.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-4 w-4 text-slate-500" />
                    <div>
                      <Label className="text-sm font-medium">{category.label}</Label>
                      <p className="text-xs text-slate-500">{category.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={localPrefs.categories[category.id]}
                    onCheckedChange={() => toggleCategory(category.id)}
                    data-testid={`category-${category.id}`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Save Button */}
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="w-full bg-[#1B2A4A] hover:bg-[#152039]"
          data-testid="save-preferences"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Preferences
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
